import { useState, useEffect } from 'react';
import { merge } from 'lodash';
import '~/apps/App.css';
import '~/apps/common.css';
import '../directorySearch.css'
import LocalSearch from '../components/LocalSearch.js';
import Grid from '@mui/material/Grid';
import {useLocation, useNavigate} from "react-router-dom";
import title from "~/helpers/title.js";
import Routing from "fos-routing";
import apiInitObject from "~/helpers/apiInitObject.js";
import respHandler from "~/helpers/respHandler.js";
import queryStrReader from "~/helpers/queryStrReader.js";
import LocalResults from "~/apps/directorysearch/components/LocalResults.js";
import LocalHits from "~/apps/directorysearch/components/LocalHits.js";
import queryStrBuilder from "~/helpers/queryStrBuilder.js";

function LocalMain({props}) {
    const location = useLocation();
    const navigate = useNavigate();

    const defaultPage = 1;
    const defaultHits = "20";

    const defaultState = {
        query: '',
        page: defaultPage,
        configure: {
            hitsPerPage: defaultHits,
        },
        menu: {
            'positions.dept_title': '',
            'positions.csu_working_title': '',
        },
    };

    const [deptTitle, setDeptTitle] = useState('');
    const [departments, setDepartments] = useState([]);
    const [deptFilterItems, setDeptFilterItems] = useState([]);
    const [deptFilter, setDeptFilter] = useState('');
    const [titleFilterItems, setTitleFilterItems] = useState([]);
    const [titleFilter, setTitleFilter] = useState('');
    const [locations, setLocations] = useState([]);
    const [hitsPerPage, setHitsPerPage] = useState(defaultHits);
    const [hits, setHits] = useState([]);
    const [displayedHits, setDisplayedHits] = useState([]);
    const [searchResults, setSearchResults] = useState({nbHits: 0});
    const [previousSearchState, setPreviousSearchState] = useState(queryStrReader(location.search));

    const pageTitle = "People Search";

    // fn for dropdown select menu and Filters
    // the parameters to this function are
    // value: actual dept/title selected
    // filter: true if it is filter / false if it is department select menu
    // type: 'deptFilter' / 'titleFilter' / ''
    const setMenuSelectItem = (value, filter, type= '') => {

        // update object to set the url
        let updatedSearchState;

        const previousSearchState = queryStrReader(location.search);
        if (!previousSearchState) {
            updatedSearchState = defaultState;
        } else {
            updatedSearchState = merge({}, defaultState, previousSearchState);
        }

        // set the object for url with correct menu item
        // if, titleFilter set 'csu_working_title'
        // else, for deptFilter and deptTitle (drop-down of departments on the first page) set 'dept_title'
        if (filter) {
            if (type === 'titleFilter') {
                updatedSearchState.menu['positions.csu_working_title'] = value;
                setTitleFilter(value);
            } else {
                updatedSearchState.menu['positions.dept_title'] = value;
                setDeptFilter(value);
            }
        } else {
            updatedSearchState.menu['positions.dept_title'] = value;
            setDeptFilter(value);
        }

        // update the url
        navigate({search: queryStrBuilder(updatedSearchState)});
    };

    const updateHitsPerPage = updatedHitsPerPage => {
        setHitsPerPage(updatedHitsPerPage || defaultHits);

        const previousSearchState = queryStrReader(location.search);
        const previousHitsPerPage = previousSearchState.configure.hitsPerPage;
        const updatedSearchState = {...previousSearchState};
        updatedSearchState.configure.hitsPerPage = updatedHitsPerPage;

        // update the url
        if (previousHitsPerPage !== updatedHitsPerPage) {
            navigate({search: queryStrBuilder(updatedSearchState)});
        }
    };

    useEffect(() => {
        document.title = title({title: 'SF State Search - ' + pageTitle});

        const currentSearchState = queryStrReader(location.search);
        const query = decodeURIComponent(currentSearchState.query);
        const deptFilter = currentSearchState.menu && currentSearchState.menu['positions.dept_title'];
        const titleFilter = currentSearchState.menu && currentSearchState.menu['positions.csu_working_title'];

        // fetch the results based on search query
        // if searchQuery is defined
        if (query && query !== 'undefined') {
            // fetch search results
            fetch(Routing.generate(
                '_directorysearchapi_search',
                {query},
                process.env.DEV
            ), apiInitObject)
                .then(respHandler)
                .then(data => {
                    const searchResults = {
                        nbHits: data.length,
                    };

                    let displayedHits;

                    if (deptFilter && titleFilter) {
                        displayedHits = data.filter(hit => hit.positions[0].csu_working_title === titleFilter)
                            .filter(hit => hit.positions[0].dept_title === deptFilter);
                    } else if (deptFilter) {
                        displayedHits = data.filter(hit => hit.positions[0].dept_title === deptFilter);
                    } else if (titleFilter) {
                        displayedHits = data.filter(hit => hit.positions[0].csu_working_title === titleFilter);
                    } else
                        displayedHits = data;

                    setHits(data);
                    setDisplayedHits(displayedHits);
                    setSearchResults(searchResults);
                    setTitleFilterItems([...new Set(displayedHits.map(obj => obj.positions).map(item => item[0].csu_working_title))]);
                    setDeptFilterItems([...new Set(displayedHits.map(obj => obj.positions).map(item => item[0].dept_title))]);
                    setTitleFilter(titleFilter);
                    setDeptFilter(deptFilter);
                }).catch(err => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    throw err;
                });
        }

        // fetch call to get departments list
        if (departments.length === 0) {
            fetch(Routing.generate(
                '_directorysearchapi_alldepartments',
                null,
                process.env.DEV
            ), apiInitObject)
                .then(respHandler)
                .then(data => {
                    setDepartments(data);
                }).catch(err => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    throw err;
                });
        }

        // fetch call to get locations
        if (locations.length === 0) {
            fetch(Routing.generate(
                '_directorysearchapi_locations',
                null,
                process.env.DEV
            ), apiInitObject)
                .then(respHandler)
                .then(data => {
                    setLocations(data.data);
                }).catch(err => {
                // eslint-disable-next-line no-console
                console.error(err);
                throw err;
            });
        }

        // fetch all staff of a particular department
        if (query === '' && (currentSearchState.menu && currentSearchState.menu['positions.dept_title'])) {
            fetch(Routing.generate(
                '_directorysearchapi_searchbydepartment',
                {query: deptTitle || (currentSearchState.menu && currentSearchState.menu['positions.dept_title'])},
                process.env.DEV
            ), apiInitObject)
                .then(respHandler)
                .then(data => {
                    const searchResults = {
                        nbHits: data.length,
                    };
                    setHits(data);
                    setDisplayedHits(data);
                    setSearchResults(searchResults);
                }).catch(err => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    throw err;
            });
        }

    }, []);

    useEffect(() => {

        let fetchQueryResults = false;

        if (
            (previousSearchState && !location.search) ||
            (!previousSearchState && location.search) ||
            (previousSearchState && location.search && previousSearchState !== location.search)
        ) {

            const currentSearchState = queryStrReader(location.search);

            // if current searchState is empty / undefined
            // or
            // search query is empty and there is no menu parameter in url i.e, deptTitle should be set to ''
            if ( !currentSearchState ||
                (currentSearchState && currentSearchState.query === '' && !currentSearchState.menu))
            {
                setDeptTitle('');
            }

            // if previousSearchState doesn't exist
            if (!previousSearchState) {

                // if currentSearchState exists and search query exists
                if (currentSearchState && currentSearchState.query !== '')
                    fetchQueryResults = true;
            }
            // if previousSearchState exists
            else {

                // if currentSearchState exists and search query exists
                // check if menu parameter exits and set deptFilter and titlefilter value acc
                if (currentSearchState && currentSearchState.query !== '') {

                    // if search query didn't change from previous search query
                    if (previousSearchState.query === currentSearchState.query) {

                        // only one filter will change at a time,
                        // check which filter changed and modify hits & filterItems acc

                        if (previousSearchState.menu || currentSearchState.menu) {
                            const newHits = hits;
                            let displayedHits;
                            const deptFilter = currentSearchState.menu && currentSearchState.menu['positions.dept_title'];
                            const titleFilter = currentSearchState.menu && currentSearchState.menu['positions.csu_working_title'];
                            let newDeptFilterItems = deptFilterItems;
                            let newTitleFilterItems = titleFilterItems;

                            if (deptFilter && titleFilter) {
                                displayedHits = newHits.filter(hit => hit.positions[0].csu_working_title === titleFilter)
                                    .filter(hit => hit.positions[0].dept_title === deptFilter);
                            } else if (deptFilter) {
                                displayedHits = newHits.filter(hit => hit.positions[0].dept_title === deptFilter);
                            } else if (titleFilter) {
                                displayedHits = newHits.filter(hit => hit.positions[0].csu_working_title === titleFilter);
                            } else {
                                displayedHits = newHits;
                            }

                            // check if deptfilter has changed
                            if (deptFilter !== (previousSearchState.menu && previousSearchState.menu['positions.dept_title'])) {
                                if (deptFilter) {
                                    const hitsForFilterItems = newHits
                                        .filter(hit => hit.positions[0].dept_title === deptFilter);
                                    newTitleFilterItems = [...new Set(hitsForFilterItems.map(obj => obj.positions)
                                        .map(item => item[0].csu_working_title))];
                                } else {
                                    newTitleFilterItems = [...new Set(newHits.map(obj => obj.positions)
                                        .map(item => item[0].csu_working_title))];
                                }
                            }

                            // if titleFilter changed
                            if (titleFilter !== (previousSearchState.menu && previousSearchState.menu['positions.csu_working_title'])) {
                                if (titleFilter) {
                                    const hitsForFilterItems = newHits
                                        .filter(hit => hit.positions[0].csu_working_title === titleFilter);
                                    newDeptFilterItems = [...new Set(hitsForFilterItems.map(obj => obj.positions)
                                        .map(item => item[0].dept_title))];
                                } else {
                                    newDeptFilterItems = [...new Set(newHits.map(obj => obj.positions)
                                        .map(item => item[0].dept_title))];
                                }
                            }

                            setDisplayedHits(displayedHits);
                            setTitleFilterItems(newTitleFilterItems);
                            setDeptFilterItems(newDeptFilterItems);
                            setDeptFilter(deptFilter);
                            setTitleFilter(titleFilter);
                        }

                    }
                    // search query changed,
                    else {
                        fetchQueryResults = true;
                    }
                } else if ( (currentSearchState.query === '' && !deptTitle) ||
                    (currentSearchState.query === '' && deptTitle && previousSearchState.query !== '')
                ) {
                    setHits([]);
                    setDisplayedHits([]);
                }
            }

            if (fetchQueryResults) {
                // fetch new results
                fetch(Routing.generate(
                    '_directorysearchapi_search',
                    {query: currentSearchState.query},
                    process.env.DEV
                ), apiInitObject)
                    .then(respHandler)
                    .then(data => {
                        const searchResults = {
                            nbHits: data.length,
                        };

                        setHits(data);
                        setDisplayedHits(data);
                        setSearchResults(searchResults);
                        // reset titleFilter and deptFilter
                        setTitleFilterItems([
                            ...new Set(data.map(obj => obj.positions).map(item => item[0].csu_working_title))
                        ]);
                        setDeptFilterItems([
                            ...new Set(data.map(obj => obj.positions).map(item => item[0].dept_title))
                        ]);
                        setTitleFilter('');
                        setDeptFilter('');
                    }).catch(err => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    throw err;
                });
            }

            const currentHitsPerPage = currentSearchState && currentSearchState.configure.hitsPerPage;
            const previousHitsPerPage = previousSearchState && previousSearchState.configure.hitsPerPage;
            if (currentHitsPerPage !== previousHitsPerPage) {
                setHitsPerPage(currentHitsPerPage);
            }

            const currentMenuPositionsDeptTitle = currentSearchState.menu && currentSearchState.menu['positions.dept_title'];
            const previousMenuPositionsDeptTitle = previousSearchState.menu && previousSearchState.menu['positions.dept_title'];
            if (currentSearchState.query === '' && currentSearchState.menu && currentMenuPositionsDeptTitle !== previousMenuPositionsDeptTitle) {
                setDeptTitle(currentMenuPositionsDeptTitle)
                fetch(Routing.generate(
                    '_directorysearchapi_searchbydepartment',
                    {query: currentMenuPositionsDeptTitle},
                    process.env.DEV
                ), apiInitObject)
                    .then(respHandler)
                    .then(data => {
                        const searchResults = {
                            nbHits: data.length,
                        };
                        setHits(data);
                        setDisplayedHits(data);
                        setSearchResults(searchResults);
                    }).catch(err => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    throw err;
                });
            }
            setPreviousSearchState(queryStrReader(location.search));
        }
    }, [location]);

    return <Grid container spacing={3}>
        <Grid size={{xs: 12, lg: 10}} className="full-width">
            <h1>People Search</h1>

            <LocalSearch defaultHits={defaultHits} {...props}/>

            <LocalResults
                departments={departments}
                selectedDept={deptTitle}
                setMenuSelectItem={setMenuSelectItem}
                titleFilterItems={titleFilterItems}
                selectedTitleFilter={titleFilter}
                deptFilterItems={deptFilterItems}
                selectedDeptFilter={deptFilter}
                hitsPerPage={hitsPerPage}
                updateHitsPerPage={updateHitsPerPage}
                searchResults={searchResults}
                searchState={queryStrReader(location.search)}
            />

            <LocalHits
                hits={displayedHits}
                hitsPerPage={hitsPerPage}
                locations={locations}
                updateHitsPerPage={updateHitsPerPage}
            />

        </Grid>
        <Grid size={{xs: 12, md: 3}}/>
    </Grid>;
}

export default LocalMain;